import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { X, Sparkles, Check } from "lucide-react";
import { motion } from "framer-motion";
import { getAttribution } from "@/hooks/useAttribution";
import { captureException } from "@/lib/sentry";

const STORAGE_KEY = "mm_email_signup";
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;
const SHOW_DELAY = 8000;

function getState(): { subscribed?: boolean; dismissedAt?: number } {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    // intentionally silent
    return {};
  }
}

function setState(patch: Partial<ReturnType<typeof getState>>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...getState(), ...patch }));
}

export function EmailSignup() {
  const [expanded, setExpanded] = useState(false);
  const [, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const state = getState();
    if (state.subscribed) return;

    const alreadyDismissed = state.dismissedAt && Date.now() - state.dismissedAt < DISMISS_DURATION;

    const tryShow = () => {
      const pwaPrompt = document.querySelector("[data-testid='pwa-install-prompt']");
      if (pwaPrompt) return false;
      setReady(true);
      if (!alreadyDismissed) setExpanded(true);
      return true;
    };

    if (alreadyDismissed) {
      setReady(true);
      return;
    }

    const timer = setTimeout(() => {
      if (!tryShow()) {
        const onPwaDismissed = () => {
          setTimeout(() => {
            tryShow();
          }, 400);
          window.removeEventListener("pwa-prompt-dismissed", onPwaDismissed);
        };
        window.addEventListener("pwa-prompt-dismissed", onPwaDismissed);
      }
    }, SHOW_DELAY);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handler = () => {
      setReady(true);
      setExpanded(prev => !prev);
    };
    window.addEventListener("open-email-signup", handler);
    return () => window.removeEventListener("open-email-signup", handler);
  }, []);

  const dismiss = () => {
    setExpanded(false);
    setState({ dismissedAt: Date.now() });
    window.dispatchEvent(new Event("email-signup-closed"));
  };

  const subscribeMutation = useMutation({
    mutationFn: async (emailAddr: string) => {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailAddr, ...getAttribution() }),
      });
      if (!res.ok) {
        const err = await res.json();
        captureException(new Error(err.error || "Failed to subscribe"));
        throw new Error(err.error || "Failed to subscribe");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setMessage(data.message || "You're on the list!");
      setTimeout(() => {
        setState({ subscribed: true });
        setExpanded(false);
      }, 3500);
    },
    onError: (err: Error) => {
      setMessage(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setMessage("");
    subscribeMutation.mutate(email);
  };

  if (getState().subscribed && !subscribeMutation.isSuccess) return null;

  return (
    <>
      {/* Panel is always mounted so the backdrop-filter compositing layer
          is pre-created. Shown/hidden via CSS transition — no JS animation
          frames on first tap. */}
      <div
        className="fixed bottom-6 right-[5.5rem] z-[60] w-[340px] max-w-[calc(100vw-7rem)]"
        data-testid={expanded ? "widget-email-signup" : undefined}
        aria-hidden={!expanded}
        // `inert` removes the subtree from the focus order and accessibility
        // tree while collapsed. Without it, axe flags `aria-hidden-focus`
        // because the close button / email input / subscribe button remain
        // focusable inside an aria-hidden container.
        inert={!expanded}
        style={{
          opacity: expanded ? 1 : 0,
          transform: expanded ? "translateY(0) scale(1)" : "translateY(16px) scale(0.96)",
          pointerEvents: expanded ? "auto" : "none",
          transition: "opacity 0.18s ease, transform 0.18s ease",
          willChange: "transform, opacity",
        }}
      >
        <div
          className="rounded-lg border border-white/15 p-6 shadow-2xl relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(16,14,26,0.97) 0%, rgba(24,14,34,0.96) 100%)",
            backdropFilter: "blur(12px) saturate(1.2)",
            WebkitBackdropFilter: "blur(12px) saturate(1.2)",
          }}
        >
          <div
            className="absolute inset-0 rounded-lg pointer-events-none"
            style={{
              background: "linear-gradient(135deg, rgba(139,92,246,0.08) 0%, transparent 50%, rgba(139,92,246,0.04) 100%)",
            }}
          />

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            className="absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition-all z-20 cursor-pointer"
            aria-label="Close"
            data-testid="button-dismiss-signup"
          >
            <X size={20} />
          </button>

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-accent" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-accent font-subheading font-bold">
                Stay in the Loop
              </span>
            </div>

            <h3 className="font-heading text-lg text-white tracking-wide mb-2 leading-snug">
              Events & Giveaways
            </h3>
            <p className="text-xs text-white/50 font-light leading-relaxed mb-5">
              Be the first to hear about flash days, guest artists, giveaways, and merch drops. No spam — just the good stuff.
            </p>

            {subscribeMutation.isSuccess ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 py-3"
              >
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                  <Check size={16} className="text-accent" />
                </div>
                <p className="text-sm text-white font-medium" data-testid="text-signup-success">{message}</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/50 focus:border-accent/50 focus:outline-none focus:bg-white/8 transition-all"
                  data-testid="input-signup-email"
                />
                <button
                  type="submit"
                  disabled={subscribeMutation.isPending}
                  className="bg-accent hover:bg-accent/80 text-white px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider font-bold font-subheading transition-all disabled:opacity-50 shrink-0 btn-cta-glow"
                  data-testid="button-subscribe"
                >
                  {subscribeMutation.isPending ? "..." : "Join"}
                </button>
              </form>
            )}

            {message && !subscribeMutation.isSuccess && (
              <p className="text-xs text-red-400 mt-2">{message}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
